import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  RefreshCw,
  Mail,
  Building2,
  User,
  Calendar,
  Award,
  AlertCircle,
  Clock
} from 'lucide-react';

const ExpiryReminderHistoryDetail = () => {
  usePageTitle('Email Preview');
  const navigate = useNavigate();
  const { historyId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [historyItem, setHistoryItem] = useState(null);

  useEffect(() => {
    fetchHistoryItem();
  }, [historyId]);

  const fetchHistoryItem = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/expiry-reminders/history/${historyId}`);
      setHistoryItem(response.data);
    } catch (error) {
      console.error('Failed to fetch history item:', error);
      toast.error('Failed to load email details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Access check
  if (!['admin', 'training_admin'].includes(user?.role)) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators and training admins can access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2C9DE1]" />
      </div>
    );
  }

  if (!historyItem) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Email Not Found</h2>
        <p className="text-slate-500 mt-2">The requested email could not be found.</p>
        <Button onClick={() => navigate('/expiry-reminder-history')} className="mt-4">
          Back to History
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/expiry-reminder-history')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email Preview
            </h1>
            <p className="text-slate-500 text-sm">View the sent expiry reminder email</p>
          </div>
        </div>
      </div>

      {/* Email Details Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="w-5 h-5 text-[#2C9DE1]" />
            Email Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Company</div>
                  <div className="font-medium">{historyItem.company_name}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Sent To</div>
                  <div className="font-medium">{historyItem.recipient_email}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Primary Contact</div>
                  <div className="font-medium">{historyItem.primary_contact || 'Not set'}</div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Sent At</div>
                  <div className="font-medium">{formatDateTime(historyItem.sent_at)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Sent By</div>
                  <div className="font-medium">{historyItem.sent_by}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Certificates Included</div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{historyItem.certificate_count}</Badge>
                    <span className="text-sm text-slate-500">
                      for {historyItem.student_count} student{historyItem.student_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <div className="text-xs text-slate-500">Type</div>
                  <Badge className={historyItem.expiry_window === -1 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {historyItem.expiry_window === -1 ? 'Already Expired' : `Expiring in ${historyItem.expiry_window} days`}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Content Preview
          </CardTitle>
          <CardDescription>
            <strong>Subject:</strong> {historyItem.subject}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyItem.email_body_html ? (
            <div 
              className="border rounded-lg p-4 bg-white"
              style={{ maxHeight: '600px', overflow: 'auto' }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: historyItem.email_body_html }}
                className="email-preview"
              />
            </div>
          ) : historyItem.email_body_text ? (
            <div className="border rounded-lg p-4 bg-slate-50">
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
                {historyItem.email_body_text}
              </pre>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>Email content not available for this record.</p>
              <p className="text-xs mt-1">Only emails sent after this update will have content stored.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpiryReminderHistoryDetail;
